// src/components/EmailReportButton.tsx
//
// ★★★ WAVE 27 — the third verb, beside Print and Download.
//
// P2C1 §12.52 (C-16) requires every query and report to be printable and
// downloadable. The owner asked for emailing it too, next to the two download
// buttons, on every screen that offers a report — nineteen of them.
//
// ══ ⚠ WHY THIS IS ONE COMPONENT AND NOT NINETEEN BUTTONS ══
//
// Every one of those screens would otherwise repeat the same four decisions: what
// the recipient field accepts, what happens while the request is in flight, what
// is shown when it succeeds, and — the one that matters — **what is said about
// whether the message will actually be delivered.** Nineteen copies is nineteen
// chances for the last one to drift into "Sent!", which would be a lie in every
// environment this repository ships.
//
// ══ ★★★ IT NEVER SAYS "SENT" ══
//
// The gateway holds no mail transport in any environment here (`DEPLOYMENT-COMPLETION.md`
// B-5: no relay, no credential, no sending domain). The route queues the message
// into the same outbox every notification uses, and returns the transport the
// composition root actually built — `none`, `simulated` or `live` — together with
// a sentence describing what that means. **This component renders the SERVER's
// sentence verbatim** rather than composing its own from the status, for the same
// reason the dashboard renders the server's numbers: a second place to decide what
// a deployment can do is a second place to be wrong about it.
//
// ⚠ **AND IT RENDERS THAT SENTENCE ON SUCCESS, NOT ONLY ON FAILURE.** A confirmation
// that says "queued — nothing will be sent, no relay is configured" is a strange
// thing to show someone who pressed Email, and it is the honest thing. An officer
// who is told "Sent" and later finds nothing arrived has been misled by the
// product; an officer told the truth can go and ask for a relay.
import { useId, useState, type ReactElement } from 'react'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { IconButton } from './IconButton'

export interface EmailReportButtonProps {
  /**
   * The export route to email, exactly as the server's allowlist spells it.
   *
   * ⚠ Matched WHOLE against `EMAILABLE_REPORTS` in `routes/report-email.ts`. A
   * path this component invents is refused by the server rather than invoked —
   * which is the correct outcome and also a dead button, so the route's own test
   * asserts every allowlisted path is genuinely served.
   */
  readonly path: string
  /**
   * The filters the screen is currently showing, so the emailed document matches
   * what the officer is looking at.
   *
   * ⚠ THE SAME OBJECT THE DOWNLOAD BUTTONS BUILD. If these two ever diverge, an
   * officer downloads one population and emails another — the defect the records
   * screen's `exportState` already exists to prevent, one surface over.
   */
  readonly query?: Readonly<Record<string, string>>
  /** Disabled with a reason, when the screen has nothing to send yet. */
  readonly disabledReason?: string
}

interface QueuedResult {
  readonly recipient: string
  readonly attachment: { readonly filename: string; readonly bytes: number }
  readonly notificationDelivery: string
  readonly note: string
}

export function EmailReportButton({ path, query, disabledReason }: EmailReportButtonProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [to, setTo] = useState('')
  const [note, setNote] = useState('')
  const [format, setFormat] = useState<'pdf' | 'xlsx'>('pdf')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [queued, setQueued] = useState<QueuedResult | null>(null)
  const panelId = useId()

  const send = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    setQueued(null)
    try {
      setQueued(await api.emailReport({ path, format, to, ...(note.length > 0 && { note }), ...(query && { query }) }))
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'The report could not be queued.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="pt-email-report">
      <IconButton
        glyph="email"
        label="Email"
        disabled={disabledReason !== undefined}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((was) => !was)}
      />
      {/*
        ⚠ A DISABLED CONTROL IS A STATEMENT, NOT A DOOR — and a disabled button is
        not focusable, so a tooltip on it is an explanation nobody can reach. The
        reason sits beside it in prose, which is the same decision `LoginPage`
        records for its federated button.
      */}
      {disabledReason !== undefined && (
        <p className="text-sm text-[var(--sgds-color-subtle)] mt-1">{disabledReason}</p>
      )}

      {open && disabledReason === undefined && (
        <div className="pt-card mt-3" id={panelId}>
          <p className="font-semibold">Email this report</p>
          <p className="text-sm text-[var(--sgds-body-color-subtle)] mt-1">
            The attachment is produced by the same route the download buttons call, restricted to what your
            current scope may already see.
          </p>

          <label className="block mt-3 text-sm" htmlFor={`${panelId}-to`}>
            Recipient
          </label>
          <input
            id={`${panelId}-to`}
            className="pt-input w-full"
            type="email"
            inputMode="email"
            placeholder="name@agency.gov.sg"
            value={to}
            onChange={(event) => setTo(event.currentTarget.value)}
          />
          {/*
            ⚠ ONE ADDRESS. The server refuses a list rather than splitting it — a
            comma is how one requested message silently becomes several — and the
            hint says so before the refusal does.
          */}
          <p className="text-xs text-[var(--sgds-color-subtle)] mt-1">
            One address. A list is refused rather than split.
          </p>

          <label className="block mt-3 text-sm" htmlFor={`${panelId}-note`}>
            Note (optional)
          </label>
          <input
            id={`${panelId}-note`}
            className="pt-input w-full"
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
          />

          <label className="block mt-3 text-sm" htmlFor={`${panelId}-format`}>
            Format
          </label>
          <select
            id={`${panelId}-format`}
            className="pt-input"
            value={format}
            onChange={(event) => setFormat(event.currentTarget.value === 'xlsx' ? 'xlsx' : 'pdf')}
          >
            <option value="pdf">PDF</option>
            <option value="xlsx">Excel</option>
          </select>

          <div className="mt-4 flex gap-2">
            <SgdsButton disabled={busy || to.length === 0} onClick={() => void send()}>
              {busy ? 'Queueing…' : 'Queue the email'}
            </SgdsButton>
            <SgdsButton variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </SgdsButton>
          </div>

          {error !== null && (
            <p className="mt-3 text-sm text-[var(--sgds-danger-color-emphasis)]" role="alert">
              {error}
            </p>
          )}

          {queued !== null && (
            <div className="mt-3" role="status">
              <p className="font-semibold">
                Queued for {queued.recipient}, {queued.attachment.filename} (
                {Math.max(1, Math.round(queued.attachment.bytes / 1024)).toLocaleString('en-SG')} KB)
              </p>
              {/*
                ⚠ THE SERVER'S OWN SENTENCE, VERBATIM. It is derived from the mail
                transport the gateway actually built — never from an environment
                name (S-016) and never composed here. In this deployment it says
                nothing will be sent, and that is the point.
              */}
              <p className="mt-1 text-sm text-[var(--sgds-body-color-subtle)]">{queued.note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
